import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Directive, ElementRef, Inject, NgZone, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appHeroScrollCue]'
})
export class HeroScrollCueDirective implements AfterViewInit, OnDestroy {
  private animationFrameId: number | null = null;
  private cueElement?: HTMLElement;
  private resizeObserver?: ResizeObserver;
  private readonly removeListeners: Array<() => void> = [];

  private readonly onViewportChange = (): void => {
    this.queueOpacityUpdate();
  };

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  ngAfterViewInit(): void {
    const host = this.elementRef.nativeElement;

    this.renderer.addClass(host, 'has-hero-scroll-cue');
    host.style.setProperty('--hero-scroll-cue-opacity', '1');
    this.cueElement = this.createCueElement();
    this.renderer.appendChild(host, this.cueElement);

    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      this.removeListeners.push(
        () => window.removeEventListener('scroll', this.onViewportChange),
        () => window.removeEventListener('resize', this.onViewportChange)
      );

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onViewportChange);
        this.resizeObserver.observe(host);
      }

      this.queueOpacityUpdate();
    });
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach((removeListener) => removeListener());
    this.resizeObserver?.disconnect();

    if (this.animationFrameId !== null) {
      window.cancelAnimationFrame(this.animationFrameId);
    }
  }

  private createCueElement(): HTMLElement {
    const cue = this.renderer.createElement('div') as HTMLElement;
    const firstArrow = this.renderer.createElement('span') as HTMLElement;
    const secondArrow = this.renderer.createElement('span') as HTMLElement;

    this.renderer.addClass(cue, 'hero-scroll-cue');
    this.renderer.setAttribute(cue, 'aria-hidden', 'true');
    this.renderer.appendChild(cue, firstArrow);
    this.renderer.appendChild(cue, secondArrow);

    return cue;
  }

  private queueOpacityUpdate(): void {
    if (this.animationFrameId !== null) {
      return;
    }

    this.animationFrameId = window.requestAnimationFrame(() => {
      this.animationFrameId = null;
      this.updateOpacity();
    });
  }

  private updateOpacity(): void {
    const host = this.elementRef.nativeElement;
    const documentElement = this.document.documentElement;
    const scrollTop = window.scrollY || documentElement.scrollTop || 0;
    const viewportHeight = window.innerHeight || documentElement.clientHeight;
    const pageCanScroll = documentElement.scrollHeight > viewportHeight + 8;

    if (!pageCanScroll) {
      this.setOpacity(0);
      return;
    }

    const heroRect = host.getBoundingClientRect();
    const heroTop = heroRect.top + scrollTop;
    const heroHeight = Math.max(host.offsetHeight, heroRect.height, 1);
    const fadeStart = Math.max(0, heroTop - this.readHeaderHeight());
    const fadeDistance = Math.max(heroHeight * 0.5, 1);
    const progress = (scrollTop - fadeStart) / fadeDistance;
    const opacity = 1 - this.clamp(progress, 0, 1);

    this.setOpacity(opacity);
  }

  private setOpacity(opacity: number): void {
    const host = this.elementRef.nativeElement;

    host.style.setProperty('--hero-scroll-cue-opacity', opacity.toFixed(3));

    if (opacity <= 0.01) {
      this.renderer.setAttribute(host, 'data-hero-scroll-cue-hidden', 'true');
      return;
    }

    this.renderer.removeAttribute(host, 'data-hero-scroll-cue-hidden');
  }

  private readHeaderHeight(): number {
    const rawHeaderHeight = getComputedStyle(this.document.documentElement)
      .getPropertyValue('--site-header-height');
    const headerHeight = Number.parseFloat(rawHeaderHeight);

    return Number.isFinite(headerHeight) ? headerHeight : 0;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
